import * as React from 'react';
import { api } from '../api';
import { formatAvailabilityFreshness } from '../freshness';
import { Icon } from '../icons.jsx';
import { ErrorState, Skeleton, useApi } from '../hooks';
import { Badge, Button, ProviderPhoto, Tag } from '../ui.jsx';

const ageSummary = (provider, searchedAges) => {
  const ages = searchedAges?.length ? searchedAges : provider.ages;
  return ages
    .map((age) => `${age}: ${provider.availability[age].spots}`)
    .join(' · ');
};

export const ComparisonScreen = ({
  go,
  providerIds,
  searchedAges,
  onBook,
  onWaitlist,
}) => {
  const { data, loading, error, reload } = useApi(() => api.providers.search({}), []);
  const providers = (data || []).filter((provider) => providerIds.includes(provider.id));

  return (
    <div className="comparison-page route-in">
      <section className="comparison-hero">
        <div className="container">
          <button className="comparison-back" type="button" onClick={() => go('results')}>
            <Icon name="chev-left" size={15}/> Back to results
          </button>
          <Badge tone="brand">Family comparison</Badge>
          <h1>Compare the details that shape your next step</h1>
          <p>
            Current openings and profile details come from providers. Official Pennsylvania
            information will remain clearly labeled when connected.
          </p>
        </div>
      </section>

      <section className="container comparison-content">
        {error && <ErrorState error={error} onRetry={reload}/>}
        {loading && (
          <div className="comparison-loading">
            <Skeleton height={420} radius={18}/>
          </div>
        )}
        {!loading && !error && providers.length < 2 && (
          <div className="comparison-empty">
            <Icon name="grid" size={28}/>
            <h2>Choose at least two providers</h2>
            <p>Select up to three providers from search results to compare them here.</p>
            <Button onClick={() => go('results')}>Choose providers</Button>
          </div>
        )}
        {!loading && !error && providers.length >= 2 && (
          <div className="comparison-table-wrap">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th><span className="sr-only">Comparison detail</span></th>
                  {providers.map((provider) => (
                    <th key={provider.id} scope="col">
                      <ProviderPhoto
                        src={provider.image}
                        alt=""
                        height={118}
                        radius={13}
                        label={provider.neighborhood}
                      />
                      <h2>{provider.name}</h2>
                      <span>{provider.distance} from your search</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="comparison-section-row">
                  <th colSpan={providers.length + 1}>
                    Provider-confirmed information
                    <Badge tone="open" icon="live">Current</Badge>
                  </th>
                </tr>
                <tr>
                  <th scope="row">Openings</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>
                      <strong className={provider.spots ? 'comparison-open' : ''}>
                        {ageSummary(provider, searchedAges)}
                      </strong>
                    </td>
                  ))}
                </tr>
                <tr>
                  <th scope="row">Last confirmed</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>
                      {formatAvailabilityFreshness(provider.availabilityUpdatedAt)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <th scope="row">Hours</th>
                  {providers.map((provider) => <td key={provider.id}>{provider.hours}</td>)}
                </tr>
                <tr>
                  <th scope="row">Languages</th>
                  {providers.map((provider) => <td key={provider.id}>{provider.languages}</td>)}
                </tr>
                <tr>
                  <th scope="row">Schedules</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>
                      <div className="comparison-tags">
                        {provider.schedules.map((schedule) => <Tag key={schedule}>{schedule}</Tag>)}
                      </div>
                    </td>
                  ))}
                </tr>
                <tr className="comparison-section-row">
                  <th colSpan={providers.length + 1}>
                    Illustrative official information
                    <Badge tone="brand" icon="shield">Pennsylvania source planned</Badge>
                  </th>
                </tr>
                <tr>
                  <th scope="row">Subsidy participation</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>{provider.subsidy ? 'Shown as accepted' : 'Not shown as accepted'}</td>
                  ))}
                </tr>
                <tr>
                  <th scope="row">Official record</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>
                      <span className="comparison-not-connected">Not connected in this prototype</span>
                    </td>
                  ))}
                </tr>
                <tr className="comparison-action-row">
                  <th scope="row">Next step</th>
                  {providers.map((provider) => (
                    <td key={provider.id}>
                      {provider.status === 'open'
                        ? <Button full onClick={() => onBook(provider.id)} icon="calendar">Request a tour</Button>
                        : <Button full variant="secondary" onClick={() => onWaitlist(provider.id)}>Join waitlist</Button>}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};
